/* eslint-disable sort-keys-fix/sort-keys-fix */
import { cn } from '@app/utils/cn';
import { getAreChildrenEmpty } from '@app/utils/getAreChildrenEmpty';
import { isDefined } from '@app/utils/is';
import { ButtonProps } from '@typings';
import React from 'react';

import { ChevronDown } from '../Icons';
import { Spinner, SpinnerVariantColor } from '../Spinner';

import { buttonStyles } from './buttonStyles';

export const Button = React.forwardRef(InnerButton) as <T extends React.ElementType = 'button'>(
  props: ButtonProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> },
) => ReturnType<typeof InnerButton>;

function InnerButton<T extends React.ElementType = 'button'>(props: ButtonProps<T>, ref: React.ForwardedRef<HTMLButtonElement>) {
  const {
    as = 'button',
    children,
    className,
    iconClassName,
    color = 'primary',
    disabled,
    icon: Icon,
    name,
    isLoading,
    size = 'large',
    variant = 'solid',
    dataTestId,
    isFluid = false,
    isChevronOpen,
    isActive = false,
    ...rest
  } = props;

  const Component = as === 'button' || disabled ? 'button' : as;
  const hasIcon = isDefined(Icon) || isLoading;
  const hasChildren = !getAreChildrenEmpty(children);
  const buttonProps = Component === 'button' ? { disabled: disabled || isLoading, type: 'button' as const } : {};
  const hasChevron = isDefined(isChevronOpen);
  const iconCommonStyles = cn('h-4 w-4 shrink-0', {
    'h-5 w-5': !hasChildren && size === 'large',
  });
  const hasIconOnly = hasIcon && !hasChildren;
  const hasChevronOnly = hasChevron && !hasIcon && !hasChildren;

  const spinnerVariantColor = {
    primary: 'light',
    secondary: 'dark',
    danger: variant === 'text' ? 'red' : 'light',
  };

  const inner = (
    <>
      {hasChildren && <span className="overflow-hidden text-ellipsis whitespace-nowrap">{children}</span>}
      {hasChevron && <ChevronDown className={cn(iconCommonStyles, 'transition-transform', { 'rotate-180': isChevronOpen })} />}
    </>
  );

  return (
    <Component
      ref={ref}
      className={cn(
        buttonStyles({
          color,
          variant,
          isDisabled: disabled,
          isLoading,
          fluid: isFluid,
          hasIconOnly,
          hasChevron,
          hasChevronOnly,
          size,
          isActive,
          className,
        }),
        className,
      )}
      data-testid={dataTestId}
      aria-label={name}
      {...buttonProps}
      {...rest}
    >
      {isDefined(Icon) && !isLoading && <Icon className={cn(iconCommonStyles, iconClassName)} />}
      {isLoading && <Spinner variant={spinnerVariantColor[color] as SpinnerVariantColor} className={iconCommonStyles} />}
      {hasChildren && hasChevron ?
        <span className="flex gap-1">{inner}</span>
      : inner}
    </Component>
  );
}
